/**
 * lnVerificationRules — BACKLOG-0501 Task A pure helpers.
 *
 * The PersonModal's Verify section reads /v3/config/ln-verification-rules
 * (via useLnVerificationRules) and resolves an applicable Mode for the
 * current Person using the four-axis matrix:
 *
 *   (cohort × citizenship × tenure × channel) → REQUIRED | OPTIONAL | PROHIBIT
 *
 * Mode drives the Run-LN button:
 *   PROHIBIT → hide the button entirely
 *   OPTIONAL → render unchanged
 *   REQUIRED → render with required-state styling + tooltip
 *
 * Axis mapping from RaoPerson:
 *   customerType === 'Minor'            → 'Minor'
 *   citizenshipStatus === 'US Citizen'  → 'Citizen'
 *   else                                 → 'Non-Citizen'
 *   mode === 'new'                       → 'New' tenure
 *   mode === 'existing'                  → 'Existing' tenure
 *
 * Channel is provided by the caller (cart context). Today ROP-teams is the
 * FSR tool so callers default to 'In-Branch'; once RCP carts carry an
 * online-origination signal, the prop value flips to 'Online' without
 * touching this resolver.
 */

import type { LnVerificationRule, LnVerificationMode } from '@retail-ops/api-client';
import type { RaoPerson } from '../../../types';

export type LnCohort = 'Personal';
export type LnCitizenship = 'Citizen' | 'Non-Citizen' | 'Minor';
export type LnTenure = 'New' | 'Existing';
export type LnChannel = 'In-Branch' | 'Online';

export interface LnRuleQuery {
  readonly cohort: LnCohort;
  readonly citizenship: LnCitizenship;
  readonly tenure: LnTenure;
  readonly channel: LnChannel;
}

export interface LnRuleResolution {
  readonly mode: LnVerificationMode;
  readonly note: string | null;
  readonly legalCitation: string;
  /** Row id; null when no row matched (safe-default fallback). */
  readonly ruleId: number | null;
}

/**
 * Safe-default when no rule matches: OPTIONAL. Matches the legacy
 * pre-BACKLOG-0501 behavior where the Run-LN button was always available.
 * Returning OPTIONAL means a misconfigured / empty rule set degrades to
 * "current behavior" rather than blocking commit or hiding the button.
 */
export const LN_RULE_FALLBACK: LnRuleResolution = {
  mode: 'OPTIONAL',
  note: null,
  legalCitation: '(no rule matched — fallback to OPTIONAL)',
  ruleId: null,
};

/**
 * Resolve the applicable rule. Prefers the most specific match: exact-on-all-
 * four-axes wins over rows with 'Any' wildcards. Ties (shouldn't happen given
 * the UX_LnVerificationRules_Tuple unique index) are broken by lowest Id —
 * stable + deterministic.
 *
 * Pure — easy to test without mounting React.
 */
export function resolveLnRule(
  rules: ReadonlyArray<LnVerificationRule>,
  query: LnRuleQuery,
): LnRuleResolution {
  const candidates = rules
    .filter((r) => r.isActive)
    .filter((r) => axisMatches(r.cohort, query.cohort))
    .filter((r) => axisMatches(r.citizenship, query.citizenship))
    .filter((r) => axisMatches(r.tenure, query.tenure))
    .filter((r) => axisMatches(r.channel, query.channel));

  if (candidates.length === 0) return LN_RULE_FALLBACK;

  // Score each: specific (non-'Any') match = 1 per axis, wildcard = 0.
  // Higher score = more specific. Tie-break by lowest Id.
  const scored = candidates.map((r) => ({
    rule: r,
    score:
      (r.cohort === 'Any' ? 0 : 1) +
      (r.citizenship === 'Any' ? 0 : 1) +
      (r.tenure === 'Any' ? 0 : 1) +
      (r.channel === 'Any' ? 0 : 1),
  }));
  scored.sort((a, b) => (b.score - a.score) || (a.rule.id - b.rule.id));
  const winner = scored[0].rule;

  return {
    mode: winner.mode,
    note: winner.note,
    legalCitation: winner.legalCitation,
    ruleId: winner.id,
  };
}

function axisMatches(ruleValue: string, queryValue: string): boolean {
  return ruleValue === 'Any' || ruleValue === queryValue;
}

/**
 * Derive the (citizenship, tenure) axes from a RaoPerson. Channel + cohort
 * come from cart-level context, so they're separate inputs to the resolver.
 *
 * Today only the 'Personal' cohort is seeded; this helper returns axes
 * suitable for that cohort. When Business / Trust cohorts land, the
 * mapping will need a customerType-aware branch.
 */
export function deriveLnAxesFromPerson(person: RaoPerson): {
  citizenship: LnCitizenship;
  tenure: LnTenure;
} {
  // Minor takes precedence over citizenship — the seed matrix encodes
  // 'Minor' as its own citizenship value, and the LegalCitation cites that
  // minors never run LN regardless of US-citizen status.
  if (person.customerType === 'Minor') {
    return { citizenship: 'Minor', tenure: tenureOf(person) };
  }
  const citizenship: LnCitizenship =
    person.citizenshipStatus === 'US Citizen' ? 'Citizen' : 'Non-Citizen';
  return { citizenship, tenure: tenureOf(person) };
}

function tenureOf(person: RaoPerson): LnTenure {
  return person.mode === 'existing' ? 'Existing' : 'New';
}
